import type { ComponentProps } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, Text, View } from 'react-native';
import { type Href, useIsFocused, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import type { OAuthGrant } from '@supabase/supabase-js';
import { trpc } from '../lib/trpc';
import { AI_PROVIDERS, getAiProviderRecipient } from '../lib/ai-provider';
import { isDemoSession, isLocalDevSession, supabase } from '../lib/supabase';
import { getLegalUrl, openExternalUrl } from '../lib/utils';
import { Button } from './Button';
import { Card } from './Card';
import { useConfirmationDialog } from './ConfirmationDialog';
import { SectionHeader } from './SectionHeader';

const CHATGPT_APP_URL = 'https://chatgpt.com/apps/per-diem/asdk_app_69ee4577626081919c98e6222ff8785c';
const AI_CONNECTORS_ROUTE = '/ai-connectors' as Href;
const PRIVACY_POLICY_URL = getLegalUrl('privacy');
const MCP_SERVER_URL = 'https://api.perdiem.app/mcp';

type AssistantAccessApi = {
  listGrants?: () => Promise<{ data: OAuthGrant[] | null; error: { message?: string } | null }>;
  revokeGrant?: (options: { clientId: string }) => Promise<{ data: unknown; error: { message?: string } | null }>;
};

type ConnectedAssistantGroup = {
  key: string;
  name: string;
  host: string | null;
  scopes: string[];
  latestGrantedAt: string;
  grants: OAuthGrant[];
};

function getAssistantAccessApi(): AssistantAccessApi | null {
  return (supabase.auth as typeof supabase.auth & { oauth?: AssistantAccessApi }).oauth ?? null;
}

function isAuthSessionMissingError(error: unknown) {
  if (!error || typeof error !== 'object') return false;
  const name = 'name' in error && typeof error.name === 'string' ? error.name : null;
  const message = 'message' in error && typeof error.message === 'string' ? error.message : null;
  return name === 'AuthSessionMissingError' || message === 'Auth session missing!';
}

function formatGrantDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Recently connected';
  return `Connected ${date.toLocaleDateString()}`;
}

function formatClientHost(uri: string | null | undefined) {
  if (!uri) return null;
  try {
    return new URL(uri).host;
  } catch {
    return uri;
  }
}

function getGrantGroupKey(grant: OAuthGrant) {
  return grant.client.name.trim().toLowerCase();
}

function mergeGrantScopes(grants: OAuthGrant[]) {
  return Array.from(new Set(grants.flatMap((grant) => grant.scopes)));
}

function getLatestGrantedAt(grants: OAuthGrant[]) {
  return grants.reduce((latest, grant) => {
    const latestTime = new Date(latest).getTime();
    const grantTime = new Date(grant.granted_at).getTime();
    if (Number.isNaN(grantTime)) return latest;
    if (Number.isNaN(latestTime) || grantTime > latestTime) return grant.granted_at;
    return latest;
  }, grants[0]?.granted_at ?? new Date().toISOString());
}

function groupAssistantGrants(grants: OAuthGrant[]): ConnectedAssistantGroup[] {
  const groups = new Map<string, OAuthGrant[]>();

  for (const grant of grants) {
    const key = getGrantGroupKey(grant);
    groups.set(key, [...(groups.get(key) ?? []), grant]);
  }

  return Array.from(groups.entries())
    .map(([key, groupGrants]) => {
      const latestGrantedAt = getLatestGrantedAt(groupGrants);
      const latestGrant =
        groupGrants.find((grant) => grant.granted_at === latestGrantedAt) ?? groupGrants[0];

      return {
        key,
        name: latestGrant?.client.name ?? 'AI chat app',
        host: formatClientHost(latestGrant?.client.uri),
        scopes: mergeGrantScopes(groupGrants),
        latestGrantedAt,
        grants: groupGrants,
      };
    })
    .sort((a, b) => {
      const aTime = new Date(a.latestGrantedAt).getTime();
      const bTime = new Date(b.latestGrantedAt).getTime();
      return (Number.isNaN(bTime) ? 0 : bTime) - (Number.isNaN(aTime) ? 0 : aTime);
    });
}

function groupMatchesName(group: ConnectedAssistantGroup, pattern: RegExp) {
  return pattern.test(group.name) || (group.host !== null && pattern.test(group.host));
}

function formatGrantDetails(scopes: string[]) {
  if (scopes.length === 0) return 'Read-only Per Diem data';
  const labels = scopes.map((scope) => {
    switch (scope) {
      case 'openid':
        return 'Per Diem identity';
      case 'email':
        return 'email address';
      case 'profile':
        return 'profile details';
      case 'phone':
        return 'phone number';
      default:
        return scope;
    }
  });
  return `Read-only Per Diem data; account details: ${labels.join(', ')}`;
}

export function AiIntegrationsCard({ isDemo = false }: { isDemo?: boolean }) {
  const isFocused = useIsFocused();
  const router = useRouter();
  const utils = trpc.useUtils();
  const { confirmDestructive } = useConfirmationDialog();
  const householdQ = trpc.households.get.useQuery(undefined, { enabled: isFocused });
  const setAiProviderMutation = trpc.households.setAiProvider.useMutation({
    onSuccess: () => {
      utils.households.get.invalidate();
    },
  });
  const setAiDataSharingConsentMutation = trpc.households.setAiDataSharingConsent.useMutation({
    onSuccess: () => {
      utils.households.get.invalidate();
    },
  });

  const selectedAiProvider = householdQ.data?.aiProvider ?? 'google';
  const hasAiDataSharingConsent = householdQ.data?.hasAiDataSharingConsent ?? false;
  const aiRecipient = getAiProviderRecipient(selectedAiProvider);
  const [assistantGrants, setAssistantGrants] = useState<OAuthGrant[]>([]);
  const [assistantGrantsLoading, setAssistantGrantsLoading] = useState(false);
  const [assistantGrantsError, setAssistantGrantsError] = useState<string | null>(null);
  const [assistantGrantsUnavailable, setAssistantGrantsUnavailable] = useState(false);
  const [assistantGrantsMessage, setAssistantGrantsMessage] = useState<string | null>(null);
  const [revokingGroupKey, setRevokingGroupKey] = useState<string | null>(null);
  const assistantGrantGroups = useMemo(
    () => groupAssistantGrants(assistantGrants),
    [assistantGrants],
  );
  const hasChatGptConnection = assistantGrantGroups.some((group) =>
    groupMatchesName(group, /chatgpt/i),
  );
  const hasGrokConnection = assistantGrantGroups.some((group) =>
    groupMatchesName(group, /grok/i),
  );

  const loadAssistantGrants = useCallback(async () => {
    const assistantAccess = getAssistantAccessApi();
    if (!assistantAccess?.listGrants) {
      setAssistantGrants([]);
      setAssistantGrantsError(null);
      setAssistantGrantsUnavailable(true);
      return;
    }

    setAssistantGrantsLoading(true);
    setAssistantGrantsError(null);
    setAssistantGrantsUnavailable(false);
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;

      const session = sessionData.session;
      if (!session || isDemoSession(session) || isLocalDevSession(session)) {
        setAssistantGrants([]);
        setAssistantGrantsUnavailable(true);
        return;
      }

      const { data, error } = await assistantAccess.listGrants();
      if (error) {
        if (isAuthSessionMissingError(error)) {
          setAssistantGrants([]);
          setAssistantGrantsUnavailable(true);
          return;
        }
        throw new Error(error.message ?? 'Could not check connected AI chat apps.');
      }
      setAssistantGrants(data ?? []);
    } catch (error) {
      if (isAuthSessionMissingError(error)) {
        setAssistantGrants([]);
        setAssistantGrantsUnavailable(true);
        return;
      }
      setAssistantGrantsError('Could not check connected AI chat apps. Please refresh and try again.');
    } finally {
      setAssistantGrantsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isFocused) return;
    void loadAssistantGrants();
  }, [isFocused, loadAssistantGrants]);

  const handleRevokeGrant = useCallback(
    async (group: ConnectedAssistantGroup) => {
      const connectionCount = group.grants.length;
      const confirmed = await confirmDestructive({
        title: `Revoke ${group.name} access?`,
        message:
          connectionCount > 1
            ? `This will remove all ${connectionCount} saved ${group.name} connections. ${group.name} will no longer be able to answer from your Per Diem data. You can connect it again later.`
            : `${group.name} will no longer be able to answer from your Per Diem data. You can connect it again later.`,
        confirmLabel: 'Revoke access',
      });
      if (!confirmed) return;

      const assistantAccess = getAssistantAccessApi();
      if (!assistantAccess?.revokeGrant) {
        setAssistantGrantsError('Connected assistant access cannot be managed in this session.');
        return;
      }

      setRevokingGroupKey(group.key);
      setAssistantGrantsError(null);
      setAssistantGrantsMessage(null);
      try {
        for (const grant of group.grants) {
          const { error } = await assistantAccess.revokeGrant({ clientId: grant.client.id });
          if (error) {
            if (isAuthSessionMissingError(error)) {
              setAssistantGrants([]);
              setAssistantGrantsUnavailable(true);
              return;
            }
            throw new Error(error.message ?? 'Could not revoke assistant access.');
          }
        }
        setAssistantGrants((current) =>
          current.filter((item) => getGrantGroupKey(item) !== group.key),
        );
        setAssistantGrantsMessage(`${group.name} access revoked.`);
      } catch (error) {
        if (isAuthSessionMissingError(error)) {
          setAssistantGrants([]);
          setAssistantGrantsUnavailable(true);
          return;
        }
        setAssistantGrantsError(
          error instanceof Error ? error.message : 'Could not revoke assistant access.',
        );
      } finally {
        setRevokingGroupKey(null);
      }
    },
    [confirmDestructive],
  );

  return (
    <Card>
      <SectionHeader title="AI Connections" />
      <Text className="text-sm leading-5 text-gray-600 dark:text-gray-300">
        Manage Per Diem's in-app AI provider and the assistant apps you have allowed to read
        Per Diem when you ask them questions.
      </Text>

      <View className="mt-4 rounded-xl bg-gray-50 px-4 py-3 dark:bg-gray-800">
        <Text className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Ask AI permission
        </Text>
        <Text className="mt-1 text-sm font-semibold text-gray-900 dark:text-gray-100">
          {hasAiDataSharingConsent ? 'Permission granted' : 'Permission required'}
        </Text>
        <Text className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          Current recipient: {aiRecipient}
        </Text>
        <Text className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          Covered data may include chat messages, balances, transactions, budgets, income, holdings,
          and FIRE plan outputs. Changing providers requires permission again.
        </Text>
      </View>

      <View className="mt-3">
        {AI_PROVIDERS.map((provider) => {
          const selected = selectedAiProvider === provider.id;
          const isPending = setAiProviderMutation.isPending
            && setAiProviderMutation.variables?.provider === provider.id;
          return (
            <Pressable
              key={provider.id}
              onPress={() => {
                if (isDemo) return;
                if (provider.id === selectedAiProvider || setAiProviderMutation.isPending) return;
                setAiProviderMutation.mutate({ provider: provider.id });
              }}
              className="flex-row items-center justify-between border-b border-gray-50 py-3 last:border-b-0 dark:border-gray-700"
            >
              <Text
                className={`text-sm ${
                  selected
                    ? 'font-semibold text-gray-900 dark:text-gray-100'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                {provider.label}
              </Text>
              <View className="w-5 items-center">
                {isPending ? (
                  <ActivityIndicator size="small" />
                ) : selected ? (
                  <Ionicons name="checkmark" size={18} color="#16a34a" />
                ) : null}
              </View>
            </Pressable>
          );
        })}
      </View>

      {setAiProviderMutation.error ? (
        <Text className="mt-2 text-xs text-red-600">
          Could not update AI provider. Please try again.
        </Text>
      ) : null}
      {setAiDataSharingConsentMutation.error ? (
        <Text className="mt-2 text-xs text-red-600">
          Could not update AI permission. Please try again.
        </Text>
      ) : null}

      {!isDemo ? (
        <View className="mt-4 gap-3">
          {hasAiDataSharingConsent ? (
            <Button
              variant="secondary"
              onPress={() => setAiDataSharingConsentMutation.mutate({ granted: false })}
              loading={setAiDataSharingConsentMutation.isPending}
            >
              Withdraw AI permission
            </Button>
          ) : (
            <Button
              onPress={() => setAiDataSharingConsentMutation.mutate({ granted: true })}
              loading={setAiDataSharingConsentMutation.isPending}
            >
              Allow AI data sharing
            </Button>
          )}
        </View>
      ) : null}

      <View className="mt-5 border-t border-gray-100 pt-4 dark:border-gray-700">
        <Text className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          External AI chat apps
        </Text>
        <Text className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-300">
          These are third-party chat apps like ChatGPT, Grok, or Claude that you have connected to
          Per Diem. They can only read Per Diem data when you ask them to use Per Diem, and you can
          revoke access here.
        </Text>

        {assistantGrantsLoading ? (
          <View className="items-center py-5">
            <ActivityIndicator />
            <Text className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Checking connected AI chat apps...
            </Text>
          </View>
        ) : assistantGrantGroups.length > 0 ? (
          <View className="mt-3 gap-3">
            {assistantGrantGroups.map((group) => (
              <ConnectedAssistantRow
                key={group.key}
                group={group}
                loading={revokingGroupKey === group.key}
                onRevoke={() => void handleRevokeGrant(group)}
              />
            ))}
          </View>
        ) : assistantGrantsUnavailable ? (
          <View className="mt-3 rounded-xl bg-gray-50 px-4 py-3 dark:bg-gray-800">
            <Text className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Connected app status unavailable
            </Text>
            <Text className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-300">
              Per Diem cannot check connected AI chat apps in this session. Sign in with your
              Per Diem account in a browser to view or revoke access here.
            </Text>
          </View>
        ) : (
          <View className="mt-3 rounded-xl bg-gray-50 px-4 py-3 dark:bg-gray-800">
            <Text className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              No external AI chat apps connected
            </Text>
            <Text className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-300">
              No external AI chat app currently has access to your Per Diem data. To add one, open
              the chat app's connector settings and connect Per Diem.
            </Text>
          </View>
        )}

        {assistantGrantsMessage ? (
          <View className="mt-3 rounded-xl bg-green-50 px-4 py-3 dark:bg-green-950">
            <Text className="text-sm text-green-700 dark:text-green-100">
              {assistantGrantsMessage}
            </Text>
          </View>
        ) : null}
        {assistantGrantsError ? (
          <View className="mt-3 rounded-xl bg-red-50 px-4 py-3 dark:bg-red-950">
            <Text className="text-sm text-red-700 dark:text-red-100">
              {assistantGrantsError}
            </Text>
          </View>
        ) : null}

        <Text className="mt-5 text-sm font-semibold text-gray-900 dark:text-gray-100">
          Add an AI chat app
        </Text>
        <Text className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-300">
          Per Diem may appear as a listed app in some assistants. In others, add it manually with
          the server URL below.
        </Text>

        <View className="mt-3 gap-3">
          {!hasChatGptConnection ? (
            <ConnectorRow
              icon="chatbubble-ellipses-outline"
              title="ChatGPT"
              description="Install the Per Diem app from the ChatGPT app directory, then approve read-only Per Diem access when prompted."
              actionLabel="Open ChatGPT app"
              onPress={() => openExternalUrl(CHATGPT_APP_URL)}
            />
          ) : null}
          {!hasGrokConnection ? (
            <ConnectorRow
              icon="sparkles-outline"
              title="Grok"
              description={`In Grok, choose Connect, select Custom, name it Per Diem, and enter ${MCP_SERVER_URL}.`}
              actionLabel="Grok setup guide"
              onPress={() => router.push(AI_CONNECTORS_ROUTE)}
            />
          ) : null}
          <ConnectorRow
            icon="link-outline"
            title="Claude and other assistants"
            description="Use the setup guide for Claude, desktop clients, or any assistant that accepts a Per Diem server URL."
            actionLabel="Setup guide"
            onPress={() => router.push(AI_CONNECTORS_ROUTE)}
          />
        </View>

        <Text className="mt-3 text-xs leading-5 text-gray-500 dark:text-gray-400">
          Server URL for manual setup: {MCP_SERVER_URL}
        </Text>
        <Pressable className="mt-2 self-start" onPress={() => openExternalUrl(PRIVACY_POLICY_URL)}>
          <Text className="text-sm font-medium text-forest-600 dark:text-forest-500">
            Review privacy policy
          </Text>
        </Pressable>
      </View>
    </Card>
  );
}

function ConnectedAssistantRow({
  group,
  loading,
  onRevoke,
}: {
  group: ConnectedAssistantGroup;
  loading: boolean;
  onRevoke: () => void;
}) {
  return (
    <View className="gap-3 rounded-xl bg-gray-50 px-4 py-3 dark:bg-gray-800">
      <View className="flex-row items-start gap-3">
        <View className="mt-0.5 h-8 w-8 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/40">
          <Ionicons name="sparkles-outline" size={17} color="#15803d" />
        </View>
        <View className="min-w-0 flex-1">
          <Text className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {group.name}
          </Text>
          {group.host ? (
            <Text className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{group.host}</Text>
          ) : null}
          <Text className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
            {formatGrantDate(group.latestGrantedAt)} - {formatGrantDetails(group.scopes)}
          </Text>
        </View>
      </View>
      <Button
        size="sm"
        variant="secondary"
        onPress={onRevoke}
        loading={loading}
        className="self-start"
      >
        Revoke access
      </Button>
    </View>
  );
}

function ConnectorRow({
  icon,
  title,
  description,
  actionLabel,
  onPress,
}: {
  icon: ComponentProps<typeof Ionicons>['name'];
  title: string;
  description: string;
  actionLabel: string;
  onPress: () => void;
}) {
  return (
    <View className="flex-row gap-3 rounded-xl bg-gray-50 px-4 py-3 dark:bg-gray-800">
      <View className="mt-0.5 h-8 w-8 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/40">
        <Ionicons name={icon} size={17} color="#15803d" />
      </View>
      <View className="min-w-0 flex-1">
        <Text className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</Text>
        <Text className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">{description}</Text>
        <Pressable className="mt-2 self-start" onPress={onPress}>
          <Text className="text-xs font-semibold text-forest-600 dark:text-forest-500">
            {actionLabel}
          </Text>
        </Pressable>
      </View>
    </View>
  );
}
